<template>
  <div class="segmentacao-page">
    <!-- Indicador de Auto-save -->
    <transition name="fade">
      <div
        v-if="salvandoRascunho"
        class="auto-save-indicator"
      >
        <b-spinner
          small
          class="mr-1"
        />
        <small>Salvando rascunho...</small>
      </div>
    </transition>

    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <p class="text-muted mb-0">
          Configure os filtros para criar uma audiência segmentada
        </p>
      </div>
      <b-button
        variant="outline-primary"
        size="sm"
        :disabled="salvandoAudiencia"
        @click="completarComIA"
      >
        <i class="feather icon-zap mr-2" />
        Completar com IA
      </b-button>
    </div>

    <!-- Seletor de Loja (aparece apenas se tiver mais de uma) -->
    <b-row
      v-if="stores.length > 1"
      class="mb-4 justify-content-center"
    >
      <b-col
        cols="12"
        md="6"
        lg="4"
      >
        <div class="store-selector text-center">
          <label class="form-label mb-1 text-muted small">Selecionar Loja:</label>
          <b-form-select
            v-model="storeUuid"
            :options="storeOptions"
            size="sm"
            @change="onStoreChange"
          >
            <template #first>
              <b-form-select-option
                :value="''"
                disabled
              >
                Escolha uma loja...
              </b-form-select-option>
            </template>
          </b-form-select>
        </div>
      </b-col>
    </b-row>

    <b-card>
      <b-card-body>
        <b-form
          id="form-segmentacao"
          @submit.prevent="criarAudiencia"
        >
          <!-- Informações Básicas -->
          <h6 class="mb-3">
            <i class="feather icon-info mr-2" />
            Informações Básicas
          </h6>

          <b-row>
            <b-col md="6">
              <b-form-group
                label="Nome da Audiência"
                label-for="nome-audiencia"
              >
                <b-form-input
                  id="nome-audiencia"
                  v-model="formulario.name"
                  placeholder="Ex: Clientes VIP"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                label="Descrição"
                label-for="descricao-audiencia"
              >
                <b-form-input
                  id="descricao-audiencia"
                  v-model="formulario.description"
                  placeholder="Descrição opcional"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </b-card-body>
    </b-card>

    <!-- Filtros de Segmentação e Produtos -->
    <b-row>
      <!-- Filtros de Segmentação (Esquerda) -->
      <b-col md="6">
        <b-card>
          <b-card-body>
            <div class="mb-3">
              <h6 class="mb-0">
                <i class="feather icon-filter mr-2" />
                Filtros de Segmentação
              </h6>
            </div>

            <b-row>
              <!-- Gênero -->
              <b-col md="12">
                <b-form-group label="Gênero">
                  <b-form-select v-model="formulario.genero">
                    <option value="">
                      Todos
                    </option>
                    <option value="M">
                      Masculino
                    </option>
                    <option value="F">
                      Feminino
                    </option>
                  </b-form-select>
                </b-form-group>
              </b-col>

              <!-- Estados -->
              <b-col md="12">
                <b-form-group>
                  <template #label>
                    <div class="d-flex justify-content-between align-items-center">
                      <span>Estados</span>
                      <div>
                        <b-button
                          size="sm"
                          variant="link"
                          class="p-0 mr-2"
                          @click="selecionarTodosEstados"
                        >
                          Selecionar Todos
                        </b-button>
                        <b-button
                          size="sm"
                          variant="link"
                          class="p-0 text-danger"
                          @click="limparEstados"
                        >
                          Limpar
                        </b-button>
                      </div>
                    </div>
                  </template>
                  <v-select
                    v-model="formulario.estados"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="estadosBrasileiros"
                    :reduce="estado => estado.value"
                    label="text"
                    multiple
                    placeholder="Selecione os estados..."
                  />
                  <small class="text-muted">
                    {{ formulario.estados.length > 0
                      ? `${formulario.estados.length} estado(s) selecionado(s)`
                      : 'Deixe vazio para incluir todos os estados'
                    }}
                  </small>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <!-- Idade -->
              <b-col md="6">
                <b-form-group label="Idade Mínima">
                  <b-form-input
                    v-model="formulario.idadeMin"
                    type="number"
                    min="0"
                    max="120"
                    placeholder="18"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Idade Máxima">
                  <b-form-input
                    v-model="formulario.idadeMax"
                    type="number"
                    min="0"
                    max="120"
                    placeholder="65"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Tipo de Cliente -->
            <b-row>
              <b-col md="12">
                <b-form-group label="Tipo de Cliente">
                  <b-form-select v-model="formulario.tipoCliente">
                    <option value="">
                      Todos
                    </option>
                    <option value="cpf">
                      Pessoa Física (CPF)
                    </option>
                    <option value="cnpj">
                      Pessoa Jurídica (CNPJ)
                    </option>
                  </b-form-select>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Filtros de Produtos (Direita) -->
      <b-col md="6">
        <b-card>
          <b-card-body>
            <div class="mb-3">
              <h6 class="mb-0">
                <i class="feather icon-shopping-cart mr-2" />
                Filtros de Produtos
              </h6>
            </div>

            <!-- Lista de Filtros Adicionados -->
            <div
              v-if="filtrosProdutos.length > 0"
              class="mb-3"
            >
              <div
                v-for="(filtro, index) in filtrosProdutos"
                :key="index"
                class="mb-2 p-2 border rounded"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div class="flex-grow-1">
                    <strong>{{ getLabelTipoFiltro(filtro.tipo) }}:</strong>
                    <span class="ml-2">{{ filtro.valor }}</span>
                  </div>
                  <b-button
                    variant="link"
                    size="sm"
                    class="text-danger p-0"
                    @click="removerFiltroProduto(index)"
                  >
                    <i class="feather icon-x" />
                  </b-button>
                </div>
              </div>
            </div>

            <!-- Formulário de Novo Filtro (visível apenas quando não há filtros ou quando mostrarFormularioFiltro é true) -->
            <div v-if="filtrosProdutos.length === 0 || mostrarFormularioFiltro">
              <!-- Tipo de Busca -->
              <b-form-group label="Buscar por">
                <b-form-select
                  v-model="novoFiltroProduto.tipo"
                >
                  <option value="">
                    Selecione o tipo de busca
                  </option>
                  <option value="nome">
                    Nome
                  </option>
                  <option value="sku">
                    SKU
                  </option>
                  <!-- <option value="produto_id">
                    ID do Produto
                  </option> -->
                  <option value="marca">
                    Marca
                  </option>
                  <option value="categoria">
                    Categoria
                  </option>
                </b-form-select>
              </b-form-group>

              <!-- Campo de Busca - Nome -->
              <b-form-group
                v-if="novoFiltroProduto.tipo === 'nome'"
                label="Nome do Produto"
              >
                <v-select
                  v-model="novoFiltroProduto.valor"
                  :options="opcoesProdutosNome"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  placeholder="Digite ou selecione o nome do produto..."
                  label="nome"
                  :reduce="produto => produto.nome"
                  @search="filtrarProdutosPorNome"
                >
                  <template #option="{ nome, marca, produto_id }">
                    <div>
                      <strong>{{ nome }}</strong>
                      <small class="text-muted d-block">{{ marca }} - ID: {{ produto_id }}</small>
                    </div>
                  </template>
                </v-select>
              </b-form-group>

              <!-- Campo de Busca - SKU -->
              <b-form-group
                v-if="novoFiltroProduto.tipo === 'sku'"
                label="SKU do Produto"
              >
                <v-select
                  v-model="novoFiltroProduto.valor"
                  :options="opcoesProdutosSku"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  placeholder="Digite ou selecione o SKU do produto..."
                  label="sku"
                  :reduce="produto => produto.sku || produto.produto_id"
                  @search="filtrarProdutosPorSku"
                >
                  <template #option="{ sku, produto_id, nome, marca }">
                    <div>
                      <strong>SKU: {{ sku || produto_id }}</strong>
                      <small class="text-muted d-block">{{ nome }} - {{ marca }}</small>
                    </div>
                  </template>
                </v-select>
                <small class="text-muted">Pressione Enter para adicionar</small>
              </b-form-group>

              <!-- Campo de Busca - ID do Produto -->
              <!-- COMENTADO: Backend não suporta produto_id como tipo de filtro
            <b-form-group
                v-if="novoFiltroProduto.tipo === 'produto_id'"
                label="ID do Produto"
              >
                <v-select
                  v-model="novoFiltroProduto.valor"
                  :options="opcoesProdutosIdProduto"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  placeholder="Digite ou selecione o ID do produto..."
                  label="produto_id"
                  :reduce="produto => produto.produto_id"
                  @search="filtrarProdutosPorIdProduto"
                >
                  <template #option="{ produto_id, nome, marca }">
                    <div>
                      <strong>ID do Produto: {{ produto_id }}</strong>
                      <small class="text-muted d-block">{{ nome }} - {{ marca }}</small>
                    </div>
                  </template>
                </v-select>
              </b-form-group>
            -->

              <!-- Campo de Busca - Marca -->
              <b-form-group
                v-if="novoFiltroProduto.tipo === 'marca'"
                label="Marca do Produto"
              >
                <v-select
                  v-model="novoFiltroProduto.valor"
                  :options="opcoesProdutosMarca"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  placeholder="Digite ou selecione a marca do produto..."
                  label="marca"
                  :reduce="produto => produto.marca"
                  @search="filtrarProdutosPorMarca"
                >
                  <template #option="{ marca, nome }">
                    <div>
                      <strong>{{ marca }}</strong>
                      <small class="text-muted d-block">{{ nome }}</small>
                    </div>
                  </template>
                </v-select>
              </b-form-group>

              <!-- Campo de Busca - Categoria -->
              <b-form-group
                v-if="novoFiltroProduto.tipo === 'categoria'"
                label="Categoria do Produto"
              >
                <v-select
                  v-model="novoFiltroProduto.valor"
                  :options="opcoesProdutosCategoria"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  placeholder="Digite ou selecione a categoria do produto..."
                  label="categoria"
                  :reduce="produto => produto.categoria"
                  @search="filtrarProdutosPorCategoria"
                  @keyup.enter.native="adicionarFiltroProduto"
                >
                  <template #option="{ categoria, nome }">
                    <div>
                      <strong>{{ categoria }}</strong>
                      <small class="text-muted d-block">{{ nome }}</small>
                    </div>
                  </template>
                </v-select>
              </b-form-group>

              <!-- Botão Adicionar (só aparece quando tem tipo e valor) -->
              <b-button
                v-if="novoFiltroProduto.tipo && novoFiltroProduto.valor"
                variant="primary"
                size="sm"
                block
                class="mt-2 p-1 w-50"
                @click="adicionarFiltroProduto"
                @keyup.enter="adicionarFiltroProduto"
              >
                <i class="feather icon-check mr-2" />
                Adicionar Filtro
              </b-button>
            </div>

            <!-- Botão Adicionar Outro Filtro (só aparece quando há filtros e o formulário está oculto) -->
            <b-button
              v-if="filtrosProdutos.length > 0 && !mostrarFormularioFiltro"
              variant="outline-primary"
              size="sm"
              block
              class="mt-2 p-1 w-50"
              @click="mostrarFormularioFiltro = true"
            >
              <i class="feather icon-plus mr-2" />
              Adicionar Outro Filtro
            </b-button>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Filtros de Compras e Valores -->
    <b-card class="mt-4">
      <b-card-body>
        <div class="mb-3">
          <h6 class="mb-0">
            <i class="feather icon-shopping-cart mr-2" />
            Filtro de Compras e Valores na Loja
          </h6>
        </div>
        <b-row>
          <!-- Compras -->
          <b-col md="3">
            <b-form-group label="Mín. Compras">
              <b-form-input
                v-model="formulario.minCompras"
                type="number"
                min="0"
                placeholder="1"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Máx. Compras">
              <b-form-input
                v-model="formulario.maxCompras"
                type="number"
                min="0"
                placeholder="100"
              />
            </b-form-group>
          </b-col>

          <!-- Valor gasto -->
          <b-col md="3">
            <b-form-group label="Valor Mínimo Gasto na Loja (R$)">
              <b-form-input
                v-model="formulario.valorMinimo"
                type="number"
                step="0.01"
                min="0"
                placeholder="100.00"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Valor Máximo Gasto na Loja (R$)">
              <b-form-input
                v-model="formulario.valorMaximo"
                type="number"
                step="0.01"
                min="0"
                placeholder="10000.00"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="3">
            <b-form-group label="Limite de Contatos">
              <b-form-input
                v-model="formulario.limite"
                type="number"
                min="1"
                max="50000"
                placeholder="1000"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Filtro por Data de Compra -->
    <b-card class="mt-4">
      <b-card-body>
        <div class="mb-3">
          <h6 class="mb-0">
            <i class="feather icon-calendar mr-2" />
            Filtro por Data de Compra
          </h6>
        </div>

        <div class="filtro-data-toggle mb-2">
          <div
            class="toggle-option"
            :class="{ active: tipoFiltroData === 'periodo' }"
            @click="alterarTipoFiltroData('periodo')"
          >
            Período de Compra
          </div>
          <div
            class="toggle-option"
            :class="{ active: tipoFiltroData === 'ultimaCompra' }"
            @click="alterarTipoFiltroData('ultimaCompra')"
          >
            Última Compra
          </div>
        </div>
        <p class="text-muted mb-3">
          {{ tipoFiltroData === 'periodo'
            ? 'Filtra quem fez qualquer compra dentro do período.'
            : 'Filtra quem teve a última compra dentro do período.' }}
        </p>

        <b-row v-if="tipoFiltroData === 'periodo'">
          <b-col md="6">
            <b-form-group label="De">
              <b-form-input
                v-model="formulario.dataInicioCompras"
                type="date"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Até">
              <b-form-input
                v-model="formulario.dataFimCompras"
                type="date"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row v-if="tipoFiltroData === 'ultimaCompra'">
          <b-col md="6">
            <b-form-group label="De">
              <b-form-input
                v-model="formulario.dataInicioUltimaCompra"
                type="date"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Até">
              <b-form-input
                v-model="formulario.dataFimUltimaCompra"
                type="date"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Exclusão de Audiências Existentes -->
    <b-card class="mt-4">
      <b-card-body>
        <div class="mb-3">
          <h6 class="mb-1">
            <i class="feather icon-user-minus mr-2" />
            Remover Leads de Outras Audiências
          </h6>
          <p class="text-muted">
            Selecione audiências cujos leads NÃO devem aparecer nesta nova audiência.
            Isso evita que o mesmo lead receba mensagens duplicadas.
          </p>
        </div>

        <b-form-group>
          <template #label>
            <div class="d-flex justify-content-between align-items-center">
              <span>Audiências</span>
              <div>
                <b-button
                  size="sm"
                  variant="link"
                  class="p-0 mr-2"
                  @click="selecionarTodasAudiencias"
                >
                  Selecionar Todas
                </b-button>
                <b-button
                  v-if="audienciasExcluir.length > 0"
                  size="sm"
                  variant="link"
                  class="p-0 text-danger"
                  @click="audienciasExcluir = []"
                >
                  Limpar
                </b-button>
              </div>
            </div>
          </template>
          <v-select
            v-model="audienciasExcluir"
            :options="audienciasParaExclusao"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :reduce="aud => aud.id"
            label="name"
            multiple
            placeholder="Selecione audiências para excluir seus leads..."
            :loading="carregandoAudiencias"
            :disabled="carregandoAudiencias"
          >
            <template #option="{ name, lead_count, created_at }">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{{ name }}</strong>
                  <small
                    v-if="lead_count"
                    class="text-muted d-block"
                  >
                    {{ lead_count }} leads
                  </small>
                </div>
                <small class="text-muted">
                  {{ formatarData(created_at) }}
                </small>
              </div>
            </template>
            <template #selected-option="{ name }">
              <span>{{ name }}</span>
            </template>
            <template #no-options>
              <span class="text-muted">Nenhuma audiência encontrada</span>
            </template>
          </v-select>
          <small
            v-if="audienciasExcluir.length > 0"
            class="text-info mt-1 d-block"
          >
            <i class="feather icon-info mr-1" />
            {{ audienciasExcluir.length }} audiência(s) selecionada(s) para exclusão
          </small>
        </b-form-group>
      </b-card-body>
    </b-card>

    <!-- Preview dos Resultados -->
    <b-card class="mt-4">
      <b-card-body>
        <!-- Preview dos Resultados da Busca -->
        <div
          v-if="previewContatos.length > 0"
          class="mb-4"
        >
          <h6 class="mb-3">
            <i class="feather icon-users mr-2" />
            Resultados da Busca ({{ previewContatos.length }} contatos encontrados)
          </h6>

          <div class="card">
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-bordered table-striped mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th
                        class="text-center"
                        style="width: 35%"
                      >
                        Nome
                      </th>
                      <th
                        class="text-center"
                        style="width: 40%"
                      >
                        Email
                      </th>
                      <th
                        class="text-center"
                        style="width: 25%"
                      >
                        Telefone
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(contato, index) in previewContatos.slice(0, 5)"
                      :key="index"
                    >
                      <td class="align-middle">
                        <strong>{{ contato.name || contato.nome || '-' }}</strong>
                      </td>
                      <td class="align-middle">
                        <span class="text-muted">{{ contato.email || '-' }}</span>
                      </td>
                      <td class="align-middle text-center">
                        <code class="text-primary">{{ contato.phone || '-' }}</code>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="card-footer bg-light">
              <div class="d-flex justify-content-between align-items-center">
                <small class="text-muted">
                  <i class="feather icon-info mr-1" />
                  Mostrando apenas os primeiros 5 resultados
                </small>
                <small class="text-success font-weight-medium">
                  Total: {{ previewContatos.length }} contatos
                </small>
              </div>
            </div>
          </div>
        </div>

        <!-- Botões -->
        <div class="d-flex justify-content-between">
          <div>
            <b-button
              variant="outline-secondary"
              :disabled="salvandoAudiencia"
              @click="testarFiltros"
            >
              <i class="feather icon-search mr-2" />
              Buscar Audiências
            </b-button>
          </div>
          <div>
            <b-button
              type="submit"
              variant="primary"
              :disabled="salvandoAudiencia || !formulario.name || !previewRealizado"
              :title="!previewRealizado ? 'Clique em Buscar Audiências primeiro' : ''"
              form="form-segmentacao"
            >
              <b-spinner
                v-if="salvandoAudiencia"
                small
                class="mr-2"
              />
              <i
                v-else
                class="feather icon-save mr-2"
              />
              {{ salvandoAudiencia ? 'Salvando...' : 'Salvar Audiência' }}
            </b-button>
          </div>
        </div>

        <!-- Aviso se não fez preview -->
        <div
          v-if="!previewRealizado"
          class="alert alert-warning d-flex align-items-center mt-3"
        >
          <i class="feather icon-alert-triangle mr-2" />
          <span>
            <strong>Atenção:</strong> Clique em "Buscar Audiências" para visualizar os contatos antes de salvar.
          </span>
        </div>
      </b-card-body>
    </b-card>

    <!-- Modal Completar com IA -->
    <b-modal
      v-model="modalIA"
      title="🤖 Completar Filtros com IA"
      size="md"
      hide-footer
      @hidden="mensagemIA = ''"
    >
      <div class="mb-3">
        <p class="text-muted mb-3">
          Descreva em linguagem natural quais clientes você quer filtrar e a IA irá configurar os filtros automaticamente.
        </p>

        <b-form-group>
          <template #label>
            <div class="d-flex justify-content-between align-items-center">
              <span>Sua solicitação:</span>
              <small :class="mensagemIA.length > 1000 ? 'text-danger' : 'text-muted'">
                {{ mensagemIA.length }}/1000 caracteres
              </small>
            </div>
          </template>

          <b-form-textarea
            v-model="mensagemIA"
            placeholder="Ex: Todos os clientes masculinos de São Paulo que compraram nos últimos 30 dias"
            rows="5"
            maxlength="1000"
            :state="mensagemIA.length > 1000 ? false : null"
          />
        </b-form-group>

        <b-form-group label="Limite de Contatos">
          <b-form-input
            v-model.number="formulario.limite"
            type="number"
            min="1"
            max="50000"
            placeholder="1000"
          />
          <small class="text-muted">Máximo 50.000 contatos por audiência</small>
        </b-form-group>

        <div class="alert alert-info">
          <strong>💡 Exemplos:</strong>
          <ul class="mb-0 mt-2">
            <li>"Todos os clientes masculinos"</li>
            <li>"Mulheres de 25 a 35 anos que compraram mais de 3 vezes"</li>
            <li>"Clientes do Rio de Janeiro e São Paulo"</li>
            <li>"Quem gastou mais de R$ 500 nos últimos 60 dias"</li>
          </ul>
        </div>
      </div>

      <div class="d-flex justify-content-end gap-2">
        <b-button
          variant="secondary"
          :disabled="processandoIA"
          @click="modalIA = false"
        >
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          :disabled="processandoIA || !mensagemIA.trim() || mensagemIA.length > 1000"
          @click="processarComIA"
        >
          <b-spinner
            v-if="processandoIA"
            small
            class="mr-2"
          />
          <i
            v-else
            class="feather icon-zap mr-2"
          />
          {{ processandoIA ? 'Processando...' : 'Processar com IA' }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import {
  createAudience,
  previewAudience,
  chatAiForSearchAudience,
  getAudiences,
} from '../../functions/basics/campaigns'
import { getInformationProducts } from '../../functions/basics/analytics'
import stores from '../../functions/basics/stores'
import { DraftCache, formatDraftAge } from '../../utils/draftCache'

export default {
  name: 'SegmentacaoPage',
  components: {
    vSelect,
  },
  data() {
    return {
      // Lojas
      stores: [],
      storeUuid: '',

      // Formulário
      formulario: {
        name: '',
        description: '',
        genero: '',
        estados: [],
        idadeMin: '',
        idadeMax: '',
        minCompras: '',
        maxCompras: '',
        valorMinimo: '',
        valorMaximo: '',
        dataInicioCompras: '',
        dataFimCompras: '',
        dataInicioUltimaCompra: '',
        dataFimUltimaCompra: '',
        tipoCliente: '',
        limite: 1000,
      },

      // Tipo de filtro de data (mutuamente exclusivos)
      tipoFiltroData: 'periodo',

      // Filtros de Produtos (múltiplos)
      filtrosProdutos: [],
      novoFiltroProduto: {
        tipo: '',
        valor: '',
      },
      mostrarFormularioFiltro: false,
      // Produtos para autocomplete
      produtosDisponiveis: [],
      buscandoProdutos: false,

      // Exclusão de audiências existentes
      audienciasDisponiveis: [],
      audienciasExcluir: [],
      carregandoAudiencias: false,

      // Sistema de Rascunho
      draftCache: null,
      salvandoRascunho: false,

      // Lista de estados brasileiros
      estadosBrasileiros: [
        { value: 'AC', text: 'Acre (AC)' },
        { value: 'AL', text: 'Alagoas (AL)' },
        { value: 'AP', text: 'Amapá (AP)' },
        { value: 'AM', text: 'Amazonas (AM)' },
        { value: 'BA', text: 'Bahia (BA)' },
        { value: 'CE', text: 'Ceará (CE)' },
        { value: 'DF', text: 'Distrito Federal (DF)' },
        { value: 'ES', text: 'Espírito Santo (ES)' },
        { value: 'GO', text: 'Goiás (GO)' },
        { value: 'MA', text: 'Maranhão (MA)' },
        { value: 'MT', text: 'Mato Grosso (MT)' },
        { value: 'MS', text: 'Mato Grosso do Sul (MS)' },
        { value: 'MG', text: 'Minas Gerais (MG)' },
        { value: 'PA', text: 'Pará (PA)' },
        { value: 'PB', text: 'Paraíba (PB)' },
        { value: 'PR', text: 'Paraná (PR)' },
        { value: 'PE', text: 'Pernambuco (PE)' },
        { value: 'PI', text: 'Piauí (PI)' },
        { value: 'RJ', text: 'Rio de Janeiro (RJ)' },
        { value: 'RN', text: 'Rio Grande do Norte (RN)' },
        { value: 'RS', text: 'Rio Grande do Sul (RS)' },
        { value: 'RO', text: 'Rondônia (RO)' },
        { value: 'RR', text: 'Roraima (RR)' },
        { value: 'SC', text: 'Santa Catarina (SC)' },
        { value: 'SP', text: 'São Paulo (SP)' },
        { value: 'SE', text: 'Sergipe (SE)' },
        { value: 'TO', text: 'Tocantins (TO)' },
      ],

      // Preview
      previewContatos: [],
      previewRealizado: false,
      salvandoAudiencia: false,

      // IA Modal
      modalIA: false,
      mensagemIA: '',
      processandoIA: false,
    }
  },

  computed: {
    storeOptions() {
      return this.stores.map(store => ({
        value: store.uuid,
        text: store.name || `Loja ${store.uuid.substring(0, 8)}`,
      }))
    },

    opcoesProdutosNome() {
      // Retornar produtos únicos por nome
      const produtosUnicos = []
      const nomesVistos = new Set()

      this.produtosDisponiveis.forEach(produto => {
        if (!nomesVistos.has(produto.nome)) {
          nomesVistos.add(produto.nome)
          produtosUnicos.push(produto)
        }
      })

      return produtosUnicos
    },

    opcoesProdutosSku() {
      // Retornar todos os produtos com SKU (cada um tem um SKU único)
      // Se o produto tiver campo 'sku', usar ele, senão usar 'produto_id'
      return this.produtosDisponiveis.map(produto => ({
        ...produto,
        sku: produto.sku || produto.produto_id,
      }))
    },

    // COMENTADO: Backend não suporta produto_id como tipo de filtro
    // opcoesProdutosIdProduto() {
    //   // Retornar todos os produtos (cada um tem um ID único)
    //   return this.produtosDisponiveis
    // },

    opcoesProdutosMarca() {
      // Retornar produtos únicos por marca
      const produtosUnicos = []
      const marcasVistas = new Set()

      this.produtosDisponiveis.forEach(produto => {
        if (!marcasVistas.has(produto.marca)) {
          marcasVistas.add(produto.marca)
          produtosUnicos.push(produto)
        }
      })

      return produtosUnicos
    },

    opcoesProdutosCategoria() {
      // Retornar produtos únicos por categoria
      const produtosUnicos = []
      const categoriasVistas = new Set()

      this.produtosDisponiveis.forEach(produto => {
        if (produto.categoria && !categoriasVistas.has(produto.categoria)) {
          categoriasVistas.add(produto.categoria)
          produtosUnicos.push(produto)
        }
      })

      return produtosUnicos
    },

    audienciasParaExclusao() {
      // Retornar audiências disponíveis para seleção de exclusão
      return this.audienciasDisponiveis.map(aud => ({
        id: aud.id,
        name: aud.name || 'Audiência sem nome',
        lead_count: aud.lead_count || 0,
        created_at: aud.created_at,
      }))
    },
  },

  watch: {
    'formulario.limite': function (newVal) {
      if (newVal && Number(newVal) > 50000) {
        this.$nextTick(() => {
          this.formulario.limite = 50000
          this.$bvToast.toast('O limite máximo é de 50.000 contatos. Valor ajustado automaticamente.', {
            title: 'Atenção',
            variant: 'warning',
            solid: true,
            autoHideDelay: 3000,
          })
        })
      }
    },

    'novoFiltroProduto.valor': function (newVal) {
      // Se o valor foi selecionado do dropdown e tem tipo, pode adicionar automaticamente
      // Mas vamos deixar o usuário clicar no botão ou pressionar Enter para ter controle
    },

    // Auto-save: Salvar rascunho quando formulário mudar
    formulario: {
      handler() {
        this.salvarRascunhoDebounced()
      },
      deep: true,
    },

    filtrosProdutos: {
      handler() {
        this.salvarRascunhoDebounced()
      },
      deep: true,
    },

    audienciasExcluir() {
      this.salvarRascunhoDebounced()
    },
  },

  async mounted() {
    try {
      // Obter UUID da rota
      this.storeUuid = this.$route.params.uuid

      // Carregar lojas
      this.stores = await stores()

      // Se não tem UUID na rota e tem lojas, usar a primeira
      if (!this.storeUuid && this.stores.length > 0) {
        this.storeUuid = this.stores[0].uuid
      }

      // Inicializar sistema de cache
      if (this.storeUuid) {
        this.draftCache = new DraftCache(`mc_draft_segmentacao_${this.storeUuid}`, 7)

        // Verificar se existe rascunho antes de carregar dados
        await this.verificarRascunho()
      }

      // Carregar produtos e audiências se tiver storeUuid
      if (this.storeUuid) {
        await Promise.all([
          this.carregarProdutos(),
          this.carregarAudienciasDisponiveis(),
        ])
      }
    } catch (error) {
      console.error('❌ Erro ao carregar lojas:', error)
      this.$bvToast.toast('Erro ao carregar lojas', {
        title: 'Erro',
        variant: 'danger',
        solid: true,
      })
    }
  },

  beforeDestroy() {
    // Salvar antes de sair se tiver dados relevantes
    const temDados = this.formulario.name
      || this.formulario.description
      || this.filtrosProdutos.length > 0
      || this.audienciasExcluir.length > 0

    if (temDados && !this.previewRealizado) {
      this.salvarRascunho()
    }
  },

  methods: {
    alterarTipoFiltroData(tipo) {
      if (this.tipoFiltroData === tipo) return
      this.tipoFiltroData = tipo
      if (tipo === 'periodo') {
        this.formulario.dataInicioUltimaCompra = ''
        this.formulario.dataFimUltimaCompra = ''
      } else {
        this.formulario.dataInicioCompras = ''
        this.formulario.dataFimCompras = ''
      }
    },

    async onStoreChange() {
      if (this.storeUuid) {
        console.log('🏪 Loja selecionada:', this.storeUuid)
        // Atualizar rota
        await this.$router.push({
          name: 'audiencia-segmentacao',
          params: { uuid: this.storeUuid },
        })
        // Carregar produtos e audiências da nova loja
        await Promise.all([
          this.carregarProdutos(),
          this.carregarAudienciasDisponiveis(),
        ])
      }
    },

    async carregarAudienciasDisponiveis() {
      if (!this.storeUuid) return

      this.carregandoAudiencias = true
      try {
        const audiencias = await getAudiences(this.storeUuid)
        this.audienciasDisponiveis = audiencias || []
        console.log(`✅ ${this.audienciasDisponiveis.length} audiências carregadas para exclusão`)
      } catch (error) {
        console.error('❌ Erro ao carregar audiências:', error)
        this.audienciasDisponiveis = []
      } finally {
        this.carregandoAudiencias = false
      }
    },

    formatarData(data) {
      if (!data) return ''
      try {
        const date = new Date(data)
        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      } catch {
        return ''
      }
    },

    async carregarProdutos() {
      if (!this.storeUuid) return

      this.buscandoProdutos = true
      try {
        const response = await getInformationProducts(this.storeUuid)
        if (response && response.success && response.data && response.data.produtos) {
          this.produtosDisponiveis = response.data.produtos
          console.log(`✅ ${this.produtosDisponiveis.length} produtos carregados`)
        }
      } catch (error) {
        console.error('❌ Erro ao carregar produtos:', error)
        this.$bvToast.toast('Erro ao carregar produtos', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000,
        })
      } finally {
        this.buscandoProdutos = false
      }
    },

    filtrarProdutosPorNome(search, loading) {
      // v-select já faz a filtragem automaticamente via computed property
      if (loading) loading(false)
    },

    filtrarProdutosPorSku(search, loading) {
      // v-select já faz a filtragem automaticamente via computed property
      if (loading) loading(false)
    },

    // COMENTADO: Backend não suporta produto_id como tipo de filtro
    // filtrarProdutosPorIdProduto(search, loading) {
    //   // v-select já faz a filtragem automaticamente via computed property
    //   if (loading) loading(false)
    // },

    filtrarProdutosPorMarca(search, loading) {
      // v-select já faz a filtragem automaticamente via computed property
      if (loading) loading(false)
    },

    filtrarProdutosPorCategoria(search, loading) {
      // v-select já faz a filtragem automaticamente via computed property
      if (loading) loading(false)
    },

    selecionarTodosEstados() {
      this.formulario.estados = this.estadosBrasileiros.map(e => e.value)
    },

    limparEstados() {
      this.formulario.estados = []
    },

    adicionarFiltroProduto() {
      if (!this.novoFiltroProduto.tipo || !this.novoFiltroProduto.valor) {
        return
      }

      this.filtrosProdutos.push({
        tipo: this.novoFiltroProduto.tipo,
        valor: this.novoFiltroProduto.valor,
      })

      // Limpar o formulário após adicionar
      this.resetarNovoFiltro()

      // Ocultar o formulário após adicionar (mostrará o botão "Adicionar outro filtro")
      this.mostrarFormularioFiltro = false
    },

    resetarNovoFiltro() {
      this.novoFiltroProduto = {
        tipo: '',
        valor: '',
      }
    },

    removerFiltroProduto(index) {
      this.filtrosProdutos.splice(index, 1)
    },

    getLabelTipoFiltro(tipo) {
      const labels = {
        nome: 'Nome',
        sku: 'SKU',
        // produto_id: 'ID do Produto', // COMENTADO: Backend não suporta produto_id
        marca: 'Marca',
        categoria: 'Categoria',
        data_ultima_venda: 'Data da Última Venda',
      }
      return labels[tipo] || tipo
    },

    completarComIA() {
      this.modalIA = true
      this.mensagemIA = ''
    },

    async processarComIA() {
      if (!this.mensagemIA.trim()) {
        this.$bvToast.toast('Digite uma descrição para a IA processar', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
          autoHideDelay: 3000,
        })
        return
      }

      if (this.mensagemIA.length > 1000) {
        this.$bvToast.toast('A mensagem não pode ter mais de 1000 caracteres', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000,
        })
        return
      }

      const limitForm = Number(this.formulario.limite)
      if (Number.isFinite(limitForm) && limitForm > 50000) {
        this.$bvToast.toast('O limite máximo é de 50.000 contatos', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000,
        })
        return
      }

      this.processandoIA = true

      try {
        const limite = Math.min(parseInt(this.formulario.limite) || 5, 50000)
        const response = await chatAiForSearchAudience(
          this.mensagemIA,
          this.storeUuid,
          limite,
        )

        if (response && response.data) {
          if (response.data.filters && response.data.filters.structured) {
            this.preencherFiltrosIA(response.data.filters.structured)
          } else if (response.data.preview) {
            this.extrairFiltrosDoSample(response.data.preview)
          }

          if (response.data.preview && response.data.preview.sample) {
            this.previewContatos = response.data.preview.sample
            this.previewRealizado = true
          }

          this.modalIA = false
          this.mensagemIA = ''

          const totalCount = response.data.preview?.total_count || 0
          this.$bvToast.toast(`Filtros aplicados! ${totalCount} contatos encontrados.`, {
            title: 'Sucesso',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000,
          })
        }
      } catch (error) {
        console.error('❌ Erro ao processar com IA:', error)
        this.$bvToast.toast('Erro ao processar sua solicitação. Tente novamente.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
        })
      } finally {
        this.processandoIA = false
      }
    },

    preencherFiltrosIA(filters) {
      if (filters.genero) this.formulario.genero = filters.genero
      if (filters.estados) {
        this.formulario.estados = Array.isArray(filters.estados)
          ? filters.estados
          : [filters.estados]
      }
      if (filters.idadeMin !== undefined) this.formulario.idadeMin = filters.idadeMin
      if (filters.idadeMax !== undefined) this.formulario.idadeMax = filters.idadeMax
      if (filters.minCompras !== undefined) this.formulario.minCompras = filters.minCompras
      if (filters.maxCompras !== undefined) this.formulario.maxCompras = filters.maxCompras
      if (filters.valorMinimo !== undefined) this.formulario.valorMinimo = filters.valorMinimo
      if (filters.valorMaximo !== undefined) this.formulario.valorMaximo = filters.valorMaximo
      if (filters.dataInicioCompras) this.formulario.dataInicioCompras = filters.dataInicioCompras
      if (filters.dataFimCompras) this.formulario.dataFimCompras = filters.dataFimCompras
      if (filters.dataInicioUltimaCompra) this.formulario.dataInicioUltimaCompra = filters.dataInicioUltimaCompra
      if (filters.dataFimUltimaCompra) this.formulario.dataFimUltimaCompra = filters.dataFimUltimaCompra
      if (filters.tipo_cliente) this.formulario.tipoCliente = filters.tipo_cliente
    },

    extrairFiltrosDoSample(preview) {
      if (!preview.sample || preview.sample.length === 0) return

      const { sample } = preview
      const generos = [...new Set(sample.map(c => c.genero).filter(Boolean))]
      if (generos.length === 1) this.formulario.genero = generos[0]

      const estados = [...new Set(sample.map(c => c.estado).filter(Boolean))]
      if (estados.length > 0) this.formulario.estados = estados

      const idades = sample.map(c => c.idade).filter(i => i !== null && i !== undefined)
      if (idades.length > 0) {
        this.formulario.idadeMin = Math.min(...idades)
        this.formulario.idadeMax = Math.max(...idades)
      }

      const totalPedidos = sample.map(c => c.total_pedidos).filter(p => p !== null && p !== undefined)
      if (totalPedidos.length > 0) {
        this.formulario.minCompras = Math.min(...totalPedidos)
        this.formulario.maxCompras = Math.max(...totalPedidos)
      }

      const valores = sample.map(c => c.valor_total).filter(v => v !== null && v !== undefined)
      if (valores.length > 0) {
        this.formulario.valorMinimo = Math.min(...valores)
        this.formulario.valorMaximo = Math.max(...valores)
      }
    },

    montarParametrosPreview(limitPadrao = 5) {
      const filtros = {}
      if (this.formulario.genero) filtros.genero = this.formulario.genero
      if (this.formulario.estados && this.formulario.estados.length > 0) {
        filtros.estado = this.formulario.estados
      }

      const idadeMin = Number(this.formulario.idadeMin)
      const idadeMax = Number(this.formulario.idadeMax)
      if (Number.isFinite(idadeMin) && idadeMin > 0) filtros.idadeMin = idadeMin
      if (Number.isFinite(idadeMax) && idadeMax > 0) filtros.idadeMax = idadeMax

      const minCompras = Number(this.formulario.minCompras)
      const maxCompras = Number(this.formulario.maxCompras)
      if (Number.isFinite(minCompras) && minCompras > 0) filtros.minCompras = minCompras
      if (Number.isFinite(maxCompras) && maxCompras > 0) filtros.maxCompras = maxCompras

      const valorMinimo = Number(this.formulario.valorMinimo)
      const valorMaximo = Number(this.formulario.valorMaximo)
      if (Number.isFinite(valorMinimo) && valorMinimo > 0) filtros.valorMinimo = valorMinimo
      if (Number.isFinite(valorMaximo) && valorMaximo > 0) filtros.valorMaximo = valorMaximo

      if (this.formulario.dataInicioCompras) filtros.dataInicioCompras = this.formulario.dataInicioCompras
      if (this.formulario.dataFimCompras) filtros.dataFimCompras = this.formulario.dataFimCompras
      if (this.formulario.dataInicioUltimaCompra) filtros.dataInicioUltimaCompra = this.formulario.dataInicioUltimaCompra
      if (this.formulario.dataFimUltimaCompra) filtros.dataFimUltimaCompra = this.formulario.dataFimUltimaCompra
      if (this.formulario.tipoCliente) filtros.tipoCliente = this.formulario.tipoCliente

      // Filtros de Produtos
      if (this.filtrosProdutos && this.filtrosProdutos.length > 0) {
        filtros.produtos = this.filtrosProdutos.map(filtro => ({
          tipo: filtro.tipo,
          valor: filtro.valor,
        }))
        console.log('🛒 [DEBUG] Filtros de produtos adicionados:', filtros.produtos)
      } else {
        console.log('⚠️ [DEBUG] Nenhum filtro de produto encontrado. this.filtrosProdutos:', this.filtrosProdutos)
      }

      // Audiências a excluir (leads que já pertencem a essas audiências)
      if (this.audienciasExcluir && this.audienciasExcluir.length > 0) {
        filtros.excluir_audiencias = this.audienciasExcluir
        console.log('🚫 [DEBUG] Audiências para exclusão:', filtros.excluir_audiencias)
      }

      const limitForm = Number(this.formulario.limite)
      const limitRaw = Number.isFinite(limitForm) && limitForm > 0 ? limitForm : limitPadrao
      const limit = Math.min(limitRaw, 50000)

      // Adicionar limite aos filtros (o backend espera filters.limite)
      filtros.limite = limit

      return { filters: filtros, limit }
    },

    async testarFiltros() {
      try {
        const limitForm = Number(this.formulario.limite)
        if (Number.isFinite(limitForm) && limitForm > 50000) {
          this.$bvToast.toast('O limite máximo é de 50.000 contatos', {
            title: 'Erro',
            variant: 'danger',
            solid: true,
            autoHideDelay: 3000,
          })
          return
        }

        // Sempre usar filters estruturados - o backend agora suporta filters.produtos
        const params = this.montarParametrosPreview(5)
        console.log('📋 [DEBUG] Preview usando filters estruturados:', params.filters)

        const response = await previewAudience(this.storeUuid, params)

        let results = []
        if (response && response.results && Array.isArray(response.results)) {
          results = response.results
        } else if (response && response.data && response.data.results && Array.isArray(response.data.results)) {
          results = response.data.results
        }

        this.previewContatos = results.map(r => ({
          nome: r.name || r.nome || r.display_name || '-',
          phone: r.phone || r.telefone || r.msisdn || '-',
          email: r.email || r.mail || '-',
          estado: r.estado || r.state || r.uf || '',
          genero: r.genero || r.gender || '',
        }))

        this.previewRealizado = true

        this.$bvToast.toast(
          `Foram encontrados ${this.previewContatos.length} contatos`,
          {
            title: 'Preview da Audiência',
            variant: 'success',
            solid: true,
          },
        )
      } catch (error) {
        console.error('❌ Erro no previewAudience:', error)
        const data = error?.response?.data || null
        const errorMessage = data?.error_message || data?.error || data?.message || error?.message || 'Erro no preview de segmentação'

        this.$bvToast.toast(errorMessage, {
          title: 'Erro no Preview',
          variant: 'danger',
          solid: true,
          autoHideDelay: 8000,
        })
      }
    },

    selecionarTodasAudiencias() {
      this.audienciasExcluir = this.audienciasParaExclusao.map(aud => aud.id)
    },

    /**
     * Verifica se existe rascunho e pergunta ao usuário
     */
    async verificarRascunho() {
      if (!this.draftCache || !this.draftCache.exists(this.storeUuid)) return

      const age = this.draftCache.getAge()
      const ageFormatted = formatDraftAge(age)

      // Usar toast com ação
      const h = this.$createElement
      const messageVNode = h('div', { class: 'd-flex flex-column' }, [
        h('p', { class: 'mb-2' }, [
          'Encontramos um rascunho salvo ',
          h('strong', ageFormatted),
          '.',
        ]),
        h('p', { class: 'text-muted small mb-0' }, 'Deseja continuar de onde parou?'),
      ])

      this.$bvToast.toast([messageVNode], {
        title: '📝 Rascunho Encontrado',
        variant: 'info',
        solid: true,
        autoHideDelay: 10000,
        toaster: 'b-toaster-top-right',
        appendToast: false,
      })

      // Aguardar um pouco e fazer a pergunta
      setTimeout(async () => {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          `Encontramos um rascunho de audiência salvo ${ageFormatted}. Deseja recuperá-lo?`,
          {
            title: '📝 Recuperar Rascunho',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'primary',
            okTitle: 'Sim, recuperar',
            cancelTitle: 'Não, começar do zero',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          },
        )

        if (confirmed) {
          this.carregarRascunho()
        } else {
          this.draftCache.clear()
          this.$bvToast.toast('Rascunho descartado', {
            title: 'Info',
            variant: 'secondary',
            solid: true,
            autoHideDelay: 2000,
          })
        }
      }, 500)
    },

    /**
     * Carrega rascunho do cache
     */
    carregarRascunho() {
      const draft = this.draftCache.load(this.storeUuid)
      if (!draft) return

      try {
        // Restaurar formulário
        if (draft.formulario) {
          this.formulario = { ...this.formulario, ...draft.formulario }
        }

        // Restaurar filtros de produtos
        if (draft.filtrosProdutos) {
          this.filtrosProdutos = draft.filtrosProdutos
        }

        // Restaurar audiências para excluir
        if (draft.audienciasExcluir) {
          this.audienciasExcluir = draft.audienciasExcluir
        }

        // Restaurar tipo de filtro de data
        if (draft.tipoFiltroData) {
          this.tipoFiltroData = draft.tipoFiltroData
        } else if (this.formulario.dataInicioUltimaCompra || this.formulario.dataFimUltimaCompra) {
          this.tipoFiltroData = 'ultimaCompra'
        }

        // Restaurar preview se existir
        if (draft.previewContatos) {
          this.previewContatos = draft.previewContatos
          this.previewRealizado = draft.previewRealizado || false
        }

        this.$bvToast.toast('Rascunho recuperado com sucesso!', {
          title: '✅ Sucesso',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000,
        })

        console.log('✅ Rascunho restaurado:', draft)
      } catch (error) {
        console.error('❌ Erro ao restaurar rascunho:', error)
        this.$bvToast.toast('Erro ao recuperar rascunho', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },

    /**
     * Salva rascunho no cache (versão imediata)
     */
    salvarRascunho() {
      if (!this.draftCache) return

      // Só salva se tiver algum dado preenchido
      const temDados = this.formulario.name
        || this.formulario.description
        || this.formulario.genero
        || this.formulario.estados.length > 0
        || this.formulario.idadeMin
        || this.formulario.idadeMax
        || this.filtrosProdutos.length > 0
        || this.audienciasExcluir.length > 0

      if (!temDados) return

      const data = {
        formulario: this.formulario,
        tipoFiltroData: this.tipoFiltroData,
        filtrosProdutos: this.filtrosProdutos,
        audienciasExcluir: this.audienciasExcluir,
        previewContatos: this.previewContatos,
        previewRealizado: this.previewRealizado,
      }

      this.draftCache.save(data, this.storeUuid)
    },

    /**
     * Salva rascunho com debounce (versão para watch)
     */
    salvarRascunhoDebounced() {
      // Mostrar indicador
      this.salvandoRascunho = true

      // Limpar timeout anterior se existir
      if (this.draftTimeout) {
        clearTimeout(this.draftTimeout)
      }

      // Criar novo timeout
      this.draftTimeout = setTimeout(() => {
        this.salvarRascunho()

        // Ocultar indicador após salvar
        setTimeout(() => {
          this.salvandoRascunho = false
        }, 500)
      }, 1000) // 1 segundo de delay
    },

    async criarAudiencia() {
      if (!this.previewRealizado) {
        this.$bvToast.toast('Clique em "Buscar Audiências" para visualizar os contatos antes de salvar.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
          autoHideDelay: 4000,
        })
        return
      }

      const limitForm = Number(this.formulario.limite)
      if (Number.isFinite(limitForm) && limitForm > 50000) {
        this.$bvToast.toast('O limite máximo é de 50.000 contatos', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000,
        })
        return
      }

      this.salvandoAudiencia = true
      try {
        // Montar parâmetros com filtros estruturados
        const params = this.montarParametrosPreview(this.formulario.limite || 1000)

        const audienceData = {
          name: this.formulario.name,
          description: this.formulario.description || null,
          filters: params.filters,
        }

        console.log('📤 [DEBUG] Enviando filtros estruturados para o backend:', JSON.stringify(audienceData, null, 2))
        const response = await createAudience(this.storeUuid, audienceData)

        if (response && response.source_kql) {
          console.log('✅ [DEBUG] Source KQL retornado:', response.source_kql.substring(0, 500))
        }

        // ✅ SUCESSO: Limpar rascunho
        if (this.draftCache) {
          this.draftCache.clear()
        }

        this.$bvToast.toast('Audiência criada com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        })

        // Redirecionar para a página de gerenciar
        await this.$router.push({
          name: 'audiencia',
          params: { uuid: this.storeUuid },
        })
      } catch (error) {
        console.error('Erro ao criar audiência:', error)
        // ❌ ERRO: Manter rascunho para não perder dados
        console.log('💾 Rascunho mantido devido ao erro')

        const errorData = error?.response?.data || null
        const errorMessage = errorData?.error_message || errorData?.error || errorData?.message || 'Erro ao criar audiência'

        this.$bvToast.toast(errorMessage, {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
        })
      } finally {
        this.salvandoAudiencia = false
      }
    },
  },
}
</script>

<style scoped>
.segmentacao-page {
  padding: 0;
}

.auto-save-indicator {
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.75);
  color: white;
  border-radius: 20px;
  font-size: 12px;
  z-index: 9999;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.filtro-data-toggle {
  display: flex;
  border: 1px solid #d8d6de;
  border-radius: 6px;
  overflow: hidden;
  max-width: 360px;
}

.filtro-data-toggle .toggle-option {
  flex: 1;
  text-align: center;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  color: #9A3092;
  background: #fff;
  user-select: none;
}

.filtro-data-toggle .toggle-option:first-child {
  border-right: 1px solid #d8d6de;
}

.filtro-data-toggle .toggle-option.active {
  color: #9A3092;
  background: #f0eeff;
  font-weight: 600;
  border-bottom: 2px solid #9A3092;
}

.filtro-data-toggle .toggle-option:hover:not(.active) {
  background: #f8f8f8;
}
</style>
